import { RootState } from '../../../store'
import {
    selectActiveChartId,
    selectChartConfig,
    selectChartTypes,
    selectDrawings,
    selectTimeBuckets,
} from '../../../selectors/chart'
import { selectPrimarySecurity, selectActiveLayout, selectActiveLayoutId } from '../../../selectors/home'
import { detectPresetCode, findPresetByCode } from './chartLayoutPresets'
import { orderedPaneSlots } from './chartLayoutCommands'
import { PriceScaleMode } from 'lightweight-charts'
import { chartTypeToJSON } from '../../../proto/generated/chart'
import { SeriesGroupCategory } from '../../../pages/home'
import { sanitizeId } from '../../../util/indicators'
import Chart from '../chart'

export type ChartContextChartTypeItem = {
    code: string
    name: string
    group?: string
}

/**
 * Live chart state only. The indicator catalog is deliberately absent: the agent
 * fetches it from `/supercharts/configuration`, and inlining it here pushed the
 * websocket frame past the server's inbound message size limit, which silently
 * dropped the whole chat request.
 */
export type ChartContextSnapshot = {
    active_chart_id: string
    charts: Record<string, ChartContextChartSnapshot>
    chart_types: ChartContextChartTypeItem[]
    time_bucket_codes: string[]
    layout: ChartContextLayout
}

/**
 * Dashboard-level window arrangement. `panes` is ordered the same way the agent
 * addresses panes (1-based, visual reading order).
 */
export type ChartContextLayout = {
    /** Preset code when the live tree matches a known arrangement, else null. */
    preset: string | null
    pane_count: number
    orientation: 'columns' | 'rows' | null
    structure: string
    focused_pane: number | null
    panes: Array<{
        pane: number
        widget_type: string
        chart_id?: string
        symbol?: string
        time_bucket?: string
        focused: boolean
    }>
    sync: Record<string, boolean>
}

export type ChartContextChartSnapshot = {
    symbol: {
        exchange: string
        code: string
        symbol?: string
        display_name?: string
        instrument?: string
    } | null
    time_bucket: {
        code: string
        name?: string
        time_series?: string
        time_value?: number
    } | null
    chart_type: string | null
    indicators: Array<{
        series_group_id: string
        code: string
        name?: string
        inputs: Record<string, string>
        pane: 'overlay' | 'new'
    }>
    comparisons: Array<{ exchange: string; code: string; display_name?: string }>
    drawings: Array<{ id: string; type: string }>
    scale_mode?: string
    /** Latest candle for the primary series, when the live chart is available. */
    last_candle?: {
        time?: number
        open?: number
        high?: number
        low?: number
        close?: number
        volume?: number
    } | null
    /** Current readout per indicator — crosshair values when hovering, else the latest bar. */
    indicator_values?: Record<string, Record<string, number>>
}

type ResolvedChartConfig = ReturnType<ReturnType<typeof selectChartConfig>>

function primaryPaneId(config: ResolvedChartConfig): string | undefined {
    if (!config) return undefined
    return Object.values(config.panesConfig || {}).find((pane) => pane.primary)?.id
}

const SCALE_MODE_NAMES: Record<number, string> = {
    [PriceScaleMode.Normal]: 'normal',
    [PriceScaleMode.Logarithmic]: 'logarithmic',
    [PriceScaleMode.Percentage]: 'percentage',
    [PriceScaleMode.IndexedTo100]: 'indexed_to_100',
}

function activeScaleMode(config: ResolvedChartConfig): string | undefined {
    const options = config?.scaleOptions
    if (!options) return undefined
    const mode = options.leftScaleVisible && !options.rightScaleVisible ? options.leftScaleMode : options.rightScaleMode
    return SCALE_MODE_NAMES[mode]
}

function round(value: unknown): number | undefined {
    const num = Number(value)
    if (!Number.isFinite(num)) return undefined
    return Math.round(num * 1e4) / 1e4
}

function lastCandleRow(chart: Chart): ChartContextChartSnapshot['last_candle'] {
    const candles = chart.getRawData() || []
    const last = candles[candles.length - 1]
    if (!last) return null
    return {
        time: round(last.time),
        open: round(last.open),
        high: round(last.high),
        low: round(last.low),
        close: round(last.close),
        volume: round(last.volume),
    }
}

/**
 * Mirrors the chart legend: the value each indicator is currently showing,
 * keyed by series group so the agent can reference it by the same id it
 * would use in a command.
 */
function indicatorValueRows(
    chart: Chart,
    config: NonNullable<ResolvedChartConfig>,
): Record<string, Record<string, number>> {
    const snapshot = chart.getChartSnapshot()
    const rows: Record<string, Record<string, number>> = {}
    Object.entries(snapshot || {}).forEach(([groupId, seriesMap]) => {
        if (groupId === 'primary') return
        const values: Record<string, number> = {}
        Object.entries(seriesMap || {}).forEach(([seriesId, points]) => {
            const latest = Array.isArray(points) ? points[points.length - 1] : undefined
            const raw = (latest as { value?: number; close?: number })?.value ?? (latest as { close?: number })?.close
            const value = round(raw)
            if (value === undefined) return
            values[config.series?.[seriesId]?.title || seriesId] = value
        })
        if (Object.keys(values).length) rows[groupId] = values
    })
    return rows
}

/** Chart id for AI context — prefers the layout focused pane over global activeChartId. */
export function resolveContextChartId(state: RootState, chartId?: string | null): string {
    const layout = selectActiveLayout(state)
    const layoutId = selectActiveLayoutId(state)
    if (layout?.activeWidget != null && layoutId) {
        const slots = orderedPaneSlots(layout)
        const focusedSlot = layout.activeWidget
        if (slots.includes(focusedSlot)) {
            const widget = layout.widgets?.[focusedSlot]
            const paneChartId = widget?.settings?.chartId ?? `${layoutId}-${focusedSlot}`
            if (paneChartId) return paneChartId
        }
    }
    return chartId || selectActiveChartId(state)
}

/**
 * Prefer a chart config that exists; fall back to global activeChartId when the
 * layout-focused pane id has not been registered yet.
 */
function resolveContextChart(
    state: RootState,
    chartId?: string | null,
): { activeChartId: string; chartConfig: ResolvedChartConfig } {
    const resolvedId = resolveContextChartId(state, chartId)
    const resolvedConfig = resolvedId ? selectChartConfig(resolvedId)(state) : undefined
    if (resolvedConfig) return { activeChartId: resolvedId, chartConfig: resolvedConfig }

    const fallbackId = selectActiveChartId(state)
    if (fallbackId && fallbackId !== resolvedId) {
        const fallbackConfig = selectChartConfig(fallbackId)(state)
        if (fallbackConfig) return { activeChartId: fallbackId, chartConfig: fallbackConfig }
    }
    return { activeChartId: resolvedId, chartConfig: resolvedConfig }
}

function contextSymbol(state: RootState): ChartContextChartSnapshot['symbol'] {
    const primary = selectPrimarySecurity(state)
    if (!primary?.primary) return null
    return {
        exchange: String(primary.primary.exchange),
        code: primary.primary.code,
        symbol: primary.primary.symbol,
        display_name: primary.displayName || primary.primary.displayName,
        instrument: primary.primary.instrument,
    }
}

export function buildChartContextSnapshot(
    state: RootState,
    chartId?: string | null,
    chart?: Chart,
): ChartContextSnapshot {
    const { activeChartId, chartConfig } = resolveContextChart(state, chartId)
    const symbol = contextSymbol(state)
    const drawingsMap = selectDrawings(state)
    const timeBuckets = selectTimeBuckets(state)
    const chartTypes = selectChartTypes(state)

    const charts: Record<string, ChartContextChartSnapshot> = {}
    if (chartConfig) {
        const primaryPane = primaryPaneId(chartConfig)
        const indicatorRows = Object.values(chartConfig.seriesGroups || {})
            .filter((group) =>
                [SeriesGroupCategory.SERIES_INDICATOR, SeriesGroupCategory.SERIES_VOLUME].includes(group.category),
            )
            .map((group) => {
                const paneConfig = Object.values(chartConfig.panesConfig || {}).find((pane) =>
                    (pane?.seriesGroups || []).includes(group.id),
                )
                const pane: 'overlay' | 'new' =
                    paneConfig?.id && primaryPane && paneConfig.id === primaryPane ? 'overlay' : 'new'
                return {
                    series_group_id: group.id,
                    code: sanitizeId(group.scriptCode),
                    name: group.name,
                    inputs: { ...(group.inputValues || {}) },
                    pane,
                }
            })

        charts[activeChartId] = {
            symbol,
            time_bucket: chartConfig.timeBucket
                ? {
                      code: chartConfig.timeBucket.code,
                      name: chartConfig.timeBucket.name,
                      time_series: String(chartConfig.timeBucket.timeSeries ?? ''),
                      time_value: chartConfig.timeBucket.timeValue,
                  }
                : null,
            chart_type: chartConfig.chartType != null ? chartTypeToJSON(chartConfig.chartType) : null,
            indicators: indicatorRows,
            comparisons: (chartConfig.comparableSecurities || []).map((security) => ({
                exchange: String(security.exchange),
                code: security.code,
                display_name: security.displayName || security.symbol,
            })),
            drawings: (drawingsMap[activeChartId] || []).slice(0, 20).map((drawing) => ({
                id: drawing.id,
                type: drawing.type,
            })),
            scale_mode: activeScaleMode(chartConfig),
            last_candle: chart ? lastCandleRow(chart) : null,
            indicator_values: chart ? indicatorValueRows(chart, chartConfig) : undefined,
        }
    } else if (symbol) {
        charts[activeChartId] = {
            symbol,
            time_bucket: null,
            chart_type: null,
            indicators: [],
            comparisons: [],
            drawings: [],
        }
    }

    return {
        active_chart_id: activeChartId,
        charts,
        chart_types: chartTypes.map((chartType) => ({
            code: chartType.code,
            name: chartType.name,
            ...(chartType.group ? { group: chartType.group } : {}),
        })),
        time_bucket_codes: timeBuckets.slice(0, 40).map((bucket) => bucket.code),
        layout: buildLayoutContext(state),
    }
}

function buildLayoutContext(state: RootState): ChartContextLayout {
    const layout = selectActiveLayout(state)
    const layoutId = selectActiveLayoutId(state)
    const slots = orderedPaneSlots(layout)
    const presetCode = detectPresetCode(layout?.panes)
    const preset = presetCode ? findPresetByCode(presetCode) : undefined
    const focusedSlot = layout?.activeWidget
    const focusedIndex = focusedSlot != null ? slots.indexOf(focusedSlot) : -1

    return {
        preset: presetCode,
        pane_count: slots.length || 1,
        orientation: preset?.orientation ?? (layout?.panes?.panes?.length ? (layout.panes.horizontal ? 'columns' : 'rows') : null),
        structure: preset?.structure ?? (slots.length > 1 ? `${slots.length} panes` : 'single pane'),
        focused_pane: focusedIndex >= 0 ? focusedIndex + 1 : null,
        panes: slots.map((slot, index) => {
            const widget = layout?.widgets?.[slot]
            const chartId = widget?.settings?.chartId ?? (layoutId ? `${layoutId}-${slot}` : undefined)
            const paneConfig = chartId ? selectChartConfig(chartId)(state) : undefined
            return {
                pane: index + 1,
                widget_type: String(widget?.widgetType ?? 'CHART'),
                ...(chartId ? { chart_id: chartId } : {}),
                ...(widget?.settings?.displayName || widget?.settings?.key
                    ? { symbol: String(widget?.settings?.displayName || widget?.settings?.key) }
                    : {}),
                ...(paneConfig?.timeBucket?.code ? { time_bucket: paneConfig.timeBucket.code } : {}),
                focused: slot === focusedSlot,
            }
        }),
        sync: {
            symbol: Boolean(layout?.syncSettings?.security),
            crosshair: Boolean(layout?.syncSettings?.crosshair),
            interval: Boolean(layout?.syncSettings?.timeBucket),
            time: Boolean(layout?.syncSettings?.time),
            drawings: Boolean(layout?.syncSettings?.drawing),
        },
    }
}

export function buildChartAiMetadata(
    state: RootState,
    chartId?: string | null,
    chart?: Chart,
): Record<string, unknown> {
    return {
        chart_context: buildChartContextSnapshot(state, chartId, chart),
    }
}
